import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

/** Material UI */
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

/** Internal Components */
import DialogHeader from 'shared/ui/DialogHeader';
import { PaperComponent } from 'shared/ui/DraggableDialog';

import { QUARTERS, SOJU_OVER_LIMIT_COLOR, fetchSojuTierAccumulation, formatProofVolume } from '../../lib/sojuTier';

const selectSx = { '& .MuiInputBase-input': { fontSize: '12px', py: '8px' } };
const headCellSx = { fontSize: '11px', fontWeight: 'bold', py: 0.8, whiteSpace: 'nowrap', textAlign: 'center' };
const cellSx = { fontSize: '11px', py: 0.8, whiteSpace: 'nowrap' };

/**
 * The quarter shown first — the last one in the response, i.e. the whole year's accumulation.
 * That is the figure the tier decision is made from; earlier quarters are one row click away.
 */
const initialQuarter = (summary) => {
    const rows = summary?.quarters ?? [];
    return rows.length ? Number(rows[rows.length - 1].quarter) : null;
};

/** Flattens the by_tier map into "S01010: 31.7007 · S01020: 0.0000". Rows with no tier arrive under the "" key. */
const formatByTier = (byTier) => {
    const entries = Object.entries(byTier ?? {});
    if (entries.length === 0) return '—';
    return entries.map(([tier, pfg]) => `${tier || 'No tier'}: ${formatProofVolume(pfg)}`).join(' · ');
};

/**
 * Dialog for looking up accumulated soju volume by year and quarter.
 *
 * The accumulation runs from 1Q of that year through the selected quarter — 1Q 5,000 plus
 * 2Q 3,000 reads as 8,000 at 2Q. Volume accumulates by each entry's import date, so an entry
 * registered this year with last year's import date counts against last year.
 */
export default function TierSearchDialog({ open, onClose, userEmail, getAccessTokenSilently }) {
    const [summary, setSummary] = useState(null);
    /** The year the operator picked — left empty at first so the server's most recent year wins. */
    const [year, setYear] = useState('');
    const [quarter, setQuarter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    /**
     * Loads the year the operator asked for, or the most recent year with soju when none was asked.
     *
     * A failed load keeps the figures already on screen. Clearing them would also empty the year
     * list the select is built from, leaving the operator with disabled controls and no way to try
     * again short of closing the dialog — so the message says how to retry instead.
     */
    const loadAccumulation = useCallback(
        async (requestedYear) => {
            if (!userEmail) return;
            setLoading(true);
            setErrorMessage('');
            try {
                const result = await fetchSojuTierAccumulation({
                    year: requestedYear,
                    getAccessTokenSilently,
                    userEmail,
                });
                setSummary(result);
                setYear(result?.year != null ? String(result.year) : '');
                setQuarter(initialQuarter(result));
            } catch (error) {
                console.error('[SojuTier] Failed to load the accumulation:', error);
                const reason = error?.message ?? 'unknown error';
                setErrorMessage(
                    `Could not load the accumulation (${reason}). Pick the year again, or close and reopen this dialog to retry.`,
                );
            } finally {
                setLoading(false);
            }
        },
        [getAccessTokenSilently, userEmail],
    );

    /** Refetched every time the operator opens it, so entries saved since the last look are counted. */
    useEffect(() => {
        if (!open) return;
        loadAccumulation(undefined);
    }, [open, loadAccumulation]);

    // The select shows the year whose figures are on screen, never a year whose load failed.
    const handleYearChange = (event) => loadAccumulation(event.target.value);

    const yearOptions = (summary?.years ?? []).map(String);
    const quarters = summary?.quarters ?? [];
    // "No soju recorded" is only claimed when the server said so (an empty year list) — never when
    // a lookup failed, which would read to the operator as if no soju had been entered.
    const showsTable = Boolean(summary) && yearOptions.length > 0 && quarters.length > 0;
    const hasNoSojuOnRecord = Boolean(summary) && !showsTable;
    const limitPfg = Number(summary?.limit_pfg);
    const selectedRow = quarters.find((row) => Number(row.quarter) === Number(quarter));
    const cumulativePfg = Number(selectedRow?.cumulative_pfg ?? 0);
    const overSelected = cumulativePfg > limitPfg;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: '18px' } }}
            PaperComponent={PaperComponent}
        >
            <DialogHeader cursor title="TIER SEARCH" onClose={onClose} />
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 3 }}>
                    <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
                        <TextField
                            select
                            size="small"
                            label="Year"
                            value={yearOptions.includes(year) ? year : ''}
                            onChange={handleYearChange}
                            disabled={loading || yearOptions.length === 0}
                            sx={{ ...selectSx, width: '120px' }}
                            InputLabelProps={{ sx: { fontSize: '12px' } }}
                        >
                            {yearOptions.map((option) => (
                                <MenuItem key={option} value={option} sx={{ fontSize: '12px' }}>
                                    {option}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="Quarter"
                            value={quarter ?? ''}
                            onChange={(event) => setQuarter(Number(event.target.value))}
                            disabled={loading || !showsTable}
                            sx={{ ...selectSx, width: '120px' }}
                            InputLabelProps={{ sx: { fontSize: '12px' } }}
                        >
                            {QUARTERS.map((option) => (
                                <MenuItem key={option} value={option} sx={{ fontSize: '12px' }}>
                                    {`${option}Q`}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>

                    {errorMessage ? (
                        <Typography
                            role="alert"
                            sx={{ fontSize: '12px', color: SOJU_OVER_LIMIT_COLOR, mb: 2 }}
                        >
                            {errorMessage}
                        </Typography>
                    ) : null}

                    {loading ? (
                        <Stack alignItems="center" sx={{ py: 6 }}>
                            <CircularProgress size={28} sx={{ color: '#00647e' }} />
                        </Stack>
                    ) : hasNoSojuOnRecord ? (
                        <Typography sx={{ fontSize: '12px', color: '#5b6b73', py: 4, textAlign: 'center' }}>
                            No soju volume has been recorded yet.
                        </Typography>
                    ) : !showsTable ? null : (
                        <>
                            <Box
                                sx={{
                                    px: 2.5,
                                    py: 2,
                                    mb: 2.5,
                                    borderRadius: '10px',
                                    bgcolor: overSelected ? '#fdeceb' : '#f4f6f7',
                                    border: `1px solid ${overSelected ? '#f0c2be' : '#e2e7ea'}`,
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography sx={{ fontSize: '11px', color: '#5b6b73' }}>
                                        {`${summary.year} cumulative through ${quarter}Q`}
                                    </Typography>
                                    <Chip
                                        data-testid="soju-tier-verdict"
                                        size="small"
                                        label={
                                            overSelected ? 'Tier 2 — limit exceeded' : 'Tier 1 — within limit'
                                        }
                                        sx={{
                                            height: '20px',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            color: 'white',
                                            bgcolor: overSelected ? SOJU_OVER_LIMIT_COLOR : '#00647e',
                                        }}
                                    />
                                </Stack>
                                <Typography
                                    data-testid="soju-cumulative-pfg"
                                    sx={{
                                        fontSize: '26px',
                                        fontWeight: 'bold',
                                        color: overSelected ? SOJU_OVER_LIMIT_COLOR : '#003a52',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {formatProofVolume(cumulativePfg)}
                                    <Typography component="span" sx={{ fontSize: '12px', ml: 0.5 }}>
                                        PFG
                                    </Typography>
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#5b6b73', mt: 0.5 }}>
                                    {overSelected
                                        ? `Over by ${formatProofVolume(cumulativePfg - limitPfg)} PFG`
                                        : `Remaining: ${formatProofVolume(limitPfg - cumulativePfg)} PFG`}
                                    {` (limit ${formatProofVolume(limitPfg)} PFG)`}
                                </Typography>
                            </Box>

                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {[
                                            'Quarter',
                                            'PFL',
                                            'PFG',
                                            'Cumulative PFL',
                                            'Cumulative PFG',
                                            'By Tier (PFG)',
                                        ].map((label) => (
                                            <TableCell key={label} align="center" sx={headCellSx}>
                                                {label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {quarters.map((row) => {
                                        const isSelected = Number(row.quarter) === Number(quarter);
                                        const rowOverLimit = Number(row.cumulative_pfg) > limitPfg;
                                        return (
                                            <TableRow
                                                key={row.quarter}
                                                hover
                                                onClick={() => setQuarter(Number(row.quarter))}
                                                sx={{
                                                    cursor: 'pointer',
                                                    bgcolor: isSelected ? '#eef4f6' : 'transparent',
                                                }}
                                            >
                                                <TableCell
                                                    sx={{ ...cellSx, fontWeight: isSelected ? 'bold' : 'normal' }}
                                                >
                                                    {row.label ?? `${summary.year}-${row.quarter}Q`}
                                                </TableCell>
                                                <TableCell align="right" sx={cellSx}>
                                                    {formatProofVolume(row.pfl)}
                                                </TableCell>
                                                <TableCell align="right" sx={cellSx}>
                                                    {formatProofVolume(row.pfg)}
                                                </TableCell>
                                                <TableCell align="right" sx={cellSx}>
                                                    {formatProofVolume(row.cumulative_pfl)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        ...cellSx,
                                                        fontWeight: isSelected ? 'bold' : 'normal',
                                                        ...(rowOverLimit && { color: SOJU_OVER_LIMIT_COLOR }),
                                                    }}
                                                >
                                                    {formatProofVolume(row.cumulative_pfg)}
                                                </TableCell>
                                                <TableCell sx={{ ...cellSx, whiteSpace: 'normal' }}>
                                                    {formatByTier(row.by_tier)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>

                            <Typography sx={{ fontSize: '10px', color: '#8a969c', mt: 2 }}>
                                Volumes accumulate by each entry&apos;s import date. Figures are rounded to four
                                decimals by the server.
                            </Typography>
                        </>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}
